import _asyncToGenerator from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _regeneratorRuntime from "/opt/build/repo/node_modules/@babel/runtime/regenerator/index.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useEffect, useLayoutEffect, createContext, useContext, useMemo, useState, createElement, useRef, useCallback } from 'react'; // Global state used to deduplicate requests and store listeners

var SWRGlobalState = new WeakMap();
var EMPTY_CACHE = {};

var noop = function noop() {}; // Using noop() as the undefined value as undefined can be replaced
// by something else. Prettier ignore and extra parentheses are necessary here
// to ensure that tsc doesn't remove the __NOINLINE__ comment.
// prettier-ignore


var UNDEFINED =
/*#__NOINLINE__*/
noop();
var OBJECT = Object;

var isUndefined = function isUndefined(v) {
  return v === UNDEFINED;
};

var isFunction = function isFunction(v) {
  return typeof v == 'function';
};

var mergeObjects = function mergeObjects(a, b) {
  return _objectSpread(_objectSpread({}, a), b);
};

var STR_UNDEFINED = 'undefined'; // NOTE: Use the function to guarantee it's re-evaluated between jsdom and node runtime for tests.

var isWindowDefined = typeof window != STR_UNDEFINED;
var isDocumentDefined = typeof document != STR_UNDEFINED;

var hasRequestAnimationFrame = function hasRequestAnimationFrame() {
  return isWindowDefined && typeof window['requestAnimationFrame'] != STR_UNDEFINED;
};

var createCacheHelper = function createCacheHelper(cache, key) {
  var state = SWRGlobalState.get(cache);
  return [// Getter
  function () {
    return cache.get(key) || EMPTY_CACHE;
  }, // Setter
  function (info) {
    var prev = cache.get(key);
    state[5](key, mergeObjects(prev, info), prev || EMPTY_CACHE);
  }, // Subscriber
  state[6]];
}; // use WeakMap to store the object->key mapping
// so the objects can be garbage collected.
// WeakMap uses a hashtable under the hood, so the lookup
// complexity is almost O(1).


var table = new WeakMap(); // counter of the key

var counter = 0; // A stable hash implementation that supports:
// - Fast and ensures unique hash properties
// - Handles unserializable values
// - Handles object key ordering
// - Generates short results
//
// This is not a serialization function, and the result is not guaranteed to be
// parsable.

var stableHash = function stableHash(arg) {
  var type = typeof arg;
  var constructor = arg && arg.constructor;
  var isDate = constructor == Date;
  var result;
  var index;

  if (OBJECT(arg) === arg && !isDate && constructor != RegExp) {
    // Object/function, not null/date/regexp. Use WeakMap to store the id first.
    // If it's already hashed, directly return the result.
    result = table.get(arg);
    if (result) return result; // Store the hash first for circular reference detection before entering the
    // recursive `stableHash` calls.
    // For other objects like set and map, we use this id directly as the hash.

    result = ++counter + '~';
    table.set(arg, result);

    if (constructor == Array) {
      // Array.
      result = '@';

      for (index = 0; index < arg.length; index++) {
        result += stableHash(arg[index]) + ',';
      }

      table.set(arg, result);
    }

    if (constructor == OBJECT) {
      // Object, sort keys.
      result = '#';
      var keys = OBJECT.keys(arg).sort();

      while (!isUndefined(index = keys.pop())) {
        if (!isUndefined(arg[index])) {
          result += index + ':' + stableHash(arg[index]) + ',';
        }
      }

      table.set(arg, result);
    }
  } else {
    result = isDate ? arg.toJSON() : type == 'symbol' ? arg.toString() : type == 'string' ? JSON.stringify(arg) : '' + arg;
  }

  return result;
};
/**
 * Due to the bug https://bugs.chromium.org/p/chromium/issues/detail?id=678075,
 * it's not reliable to detect if the browser is currently online or offline
 * based on `navigator.onLine`.
 * As a workaround, we always assume it's online on the first load, and change
 * the status upon `online` or `offline` events.
 */


var online = true;

var isOnline = function isOnline() {
  return online;
}; // For node and React Native, `add/removeEventListener` doesn't exist on window.


var _ref = isWindowDefined && window.addEventListener ? [window.addEventListener.bind(window), window.removeEventListener.bind(window)] : [noop, noop],
    _ref2 = _slicedToArray(_ref, 2),
    onWindowEvent = _ref2[0],
    offWindowEvent = _ref2[1];

var isVisible = function isVisible() {
  var visibilityState = isDocumentDefined && document.visibilityState;
  return isUndefined(visibilityState) || visibilityState !== 'hidden';
};

var initFocus = function initFocus(callback) {
  // focus revalidate
  if (isDocumentDefined) {
    document.addEventListener('visibilitychange', callback);
  }

  onWindowEvent('focus', callback);
  return function () {
    if (isDocumentDefined) {
      document.removeEventListener('visibilitychange', callback);
    }

    offWindowEvent('focus', callback);
  };
};

var initReconnect = function initReconnect(callback) {
  // revalidate on reconnected
  var onOnline = function onOnline() {
    online = true;
    callback();
  }; // nothing to revalidate, just update the status


  var onOffline = function onOffline() {
    online = false;
  };

  onWindowEvent('online', onOnline);
  onWindowEvent('offline', onOffline);
  return function () {
    offWindowEvent('online', onOnline);
    offWindowEvent('offline', onOffline);
  };
};

var preset = {
  isOnline: isOnline,
  isVisible: isVisible
};
var defaultConfigOptions = {
  initFocus: initFocus,
  initReconnect: initReconnect
};
var IS_REACT_LEGACY = !React.useId;
var IS_SERVER = !isWindowDefined || 'Deno' in window; // Polyfill requestAnimationFrame

var rAF = function rAF(f) {
  return hasRequestAnimationFrame() ? window['requestAnimationFrame'](f) : setTimeout(f, 1);
}; // React currently throws a warning when using useLayoutEffect on the server.
// To get around it, we can conditionally useEffect on the server (no-op) and
// useLayoutEffect in the browser.


var useIsomorphicLayoutEffect = IS_SERVER ? useEffect : useLayoutEffect; // This assignment is to extend the Navigator type to use effectiveType.

var navigatorConnection = typeof navigator !== 'undefined' && navigator.connection; // Adjust the config based on slow connection status (<= 70Kbps).

var slowConnection = !IS_SERVER && navigatorConnection && (['slow-2g', '2g'].includes(navigatorConnection.effectiveType) || navigatorConnection.saveData);

var serialize = function serialize(key) {
  if (isFunction(key)) {
    try {
      key = key();
    } catch (err) {
      // dependencies not ready
      key = '';
    }
  } // Use the original key as the argument of fetcher. This can be a string or an
  // array of values.


  var args = key; // If key is not falsy, or not an empty array, hash it.

  key = typeof key == 'string' ? key : (Array.isArray(key) ? key.length : key) ? stableHash(key) : '';
  return [key, args];
}; // Global timestamp.


var __timestamp = 0;

var getTimestamp = function getTimestamp() {
  return ++__timestamp;
};

var FOCUS_EVENT = 0;
var RECONNECT_EVENT = 1;
var MUTATE_EVENT = 2;
var constants = {
  __proto__: null,
  FOCUS_EVENT: FOCUS_EVENT,
  RECONNECT_EVENT: RECONNECT_EVENT,
  MUTATE_EVENT: MUTATE_EVENT
};

function internalMutate() {
  return _internalMutate.apply(this, arguments);
}

function _internalMutate() {
  _internalMutate = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var _len,
        args,
        _key2,
        cache,
        _key,
        _data,
        _opts,
        options,
        populateCache,
        rollbackOnErrorOption,
        optimisticData,
        revalidate,
        rollbackOnError,
        throwOnError,
        keyFilter,
        matchedKeys,
        it,
        keyIt,
        key,
        mutateByKey,
        _mutateByKey,
        _args2 = arguments;

    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _mutateByKey = function _mutateByKey3() {
              _mutateByKey = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_k) {
                var _serialize, _serialize2, key, _createCacheHelper, _createCacheHelper2, get, set, _SWRGlobalState$get5, _SWRGlobalState$get6, EVENT_REVALIDATORS, MUTATION, FETCH, revalidators, startRevalidate, data, error, beforeMutationTs, hasOptimisticData, state, displayedData, currentData, committedData, res;

                return _regeneratorRuntime.wrap(function _callee$(_context) {
                  while (1) {
                    switch (_context.prev = _context.next) {
                      case 0:
                        // Serialize key
                        _serialize = serialize(_k), _serialize2 = _slicedToArray(_serialize, 1), key = _serialize2[0];

                        if (key) {
                          _context.next = 3;
                          break;
                        }

                        return _context.abrupt("return");

                      case 3:
                        _createCacheHelper = createCacheHelper(cache, key), _createCacheHelper2 = _slicedToArray(_createCacheHelper, 2), get = _createCacheHelper2[0], set = _createCacheHelper2[1];
                        _SWRGlobalState$get5 = SWRGlobalState.get(cache), _SWRGlobalState$get6 = _slicedToArray(_SWRGlobalState$get5, 3), EVENT_REVALIDATORS = _SWRGlobalState$get6[0], MUTATION = _SWRGlobalState$get6[1], FETCH = _SWRGlobalState$get6[2];
                        revalidators = EVENT_REVALIDATORS[key];

                        startRevalidate = function startRevalidate() {
                          if (revalidate) {
                            // Invalidate the key by deleting the concurrent request markers so new
                            // requests will not be deduped.
                            delete FETCH[key];

                            if (revalidators && revalidators[0]) {
                              return revalidators[0](MUTATE_EVENT).then(function () {
                                return get().data;
                              });
                            }
                          }

                          return get().data;
                        }; // If there is no new data provided, revalidate the key with current state.


                        if (!(args.length < 3)) {
                          _context.next = 9;
                          break;
                        }

                        return _context.abrupt("return", startRevalidate());

                      case 9:
                        data = _data;
                        // Update global timestamps.
                        beforeMutationTs = getTimestamp();
                        MUTATION[key] = [beforeMutationTs, 0];
                        hasOptimisticData = !isUndefined(optimisticData);
                        state = get(); // `displayedData` is the current value on screen. It could be the optimistic value
                        // that is going to be overridden by a `committedData`, or get reverted back.
                        // `committedData` is the validated value that comes from a fetch or mutation.

                        displayedData = state.data;
                        currentData = state._c;
                        committedData = isUndefined(currentData) ? displayedData : currentData; // Do optimistic data update.

                        if (hasOptimisticData) {
                          optimisticData = isFunction(optimisticData) ? optimisticData(committedData) : optimisticData; // When we set optimistic data, backup the current committedData data in `_c`.

                          set({
                            data: optimisticData,
                            _c: committedData
                          });
                        }

                        if (isFunction(data)) {
                          // `data` is a function, call it passing current cache value.
                          try {
                            data = data(committedData);
                          } catch (err) {
                            // If it throws an error synchronously, we shouldn't update the cache.
                            error = err;
                          }
                        } // `data` is a promise/thenable, resolve the final data first.


                        if (!(data && isFunction(data.then))) {
                          _context.next = 30;
                          break;
                        }

                        _context.next = 22;
                        return data.catch(function (err) {
                          error = err;
                        });

                      case 22:
                        data = _context.sent;

                        if (!(beforeMutationTs !== MUTATION[key][0])) {
                          _context.next = 29;
                          break;
                        }

                        if (!error) {
                          _context.next = 26;
                          break;
                        }

                        throw error;

                      case 26:
                        return _context.abrupt("return", data);

                      case 29:
                        if (error && hasOptimisticData && rollbackOnError(error)) {
                          // Rollback. Always populate the cache in this case but without
                          // transforming the data.
                          populateCache = true;
                          data = committedData; // Reset data to be the latest committed data, and clear the `_c` value.

                          set({
                            data: data,
                            _c: UNDEFINED
                          });
                        }

                      case 30:
                        // If we should write back the cache after request.
                        if (populateCache) {
                          if (!error) {
                            // Transform the result into data.
                            if (isFunction(populateCache)) {
                              data = populateCache(data, committedData);
                            } // Only update cached data if there's no error. Data can be `undefined` here.


                            set({
                              data: data,
                              _c: UNDEFINED
                            });
                          }
                        } // Reset the timestamp to mark the mutation has ended.


                        MUTATION[key][1] = getTimestamp(); // Update existing SWR Hooks' internal states:

                        _context.next = 34;
                        return startRevalidate();

                      case 34:
                        res = _context.sent;
                        // The mutation and revalidation are ended, we can clear it since the data is
                        // not an optimistic value anymore.
                        set({
                          _c: UNDEFINED
                        }); // Throw error or return data

                        if (!error) {
                          _context.next = 40;
                          break;
                        }

                        if (!throwOnError) {
                          _context.next = 39;
                          break;
                        }

                        throw error;

                      case 39:
                        return _context.abrupt("return");

                      case 40:
                        return _context.abrupt("return", populateCache ? res : data);

                      case 41:
                      case "end":
                        return _context.stop();
                    }
                  }
                }, _callee);
              }));
              return _mutateByKey.apply(this, arguments);
            };

            mutateByKey = function _mutateByKey2(_x) {
              return _mutateByKey.apply(this, arguments);
            };

            for (_len = _args2.length, args = new Array(_len), _key2 = 0; _key2 < _len; _key2++) {
              args[_key2] = _args2[_key2];
            }

            cache = args[0], _key = args[1], _data = args[2], _opts = args[3]; // When passing as a boolean, it's explicitly used to disable/enable
            // revalidation.

            options = mergeObjects({
              populateCache: true,
              throwOnError: true
            }, typeof _opts === 'boolean' ? {
              revalidate: _opts
            } : _opts || {});
            populateCache = options.populateCache;
            rollbackOnErrorOption = options.rollbackOnError;
            optimisticData = options.optimisticData;
            revalidate = options.revalidate !== false;

            rollbackOnError = function rollbackOnError(error) {
              return typeof rollbackOnErrorOption === 'function' ? rollbackOnErrorOption(error) : rollbackOnErrorOption !== false;
            };

            throwOnError = options.throwOnError; // If the second argument is a key filter, return the mutation results for all
            // filtered keys.

            if (!isFunction(_key)) {
              _context2.next = 17;
              break;
            }

            keyFilter = _key;
            matchedKeys = [];
            it = cache.keys();

            for (keyIt = it.next(); !keyIt.done; keyIt = it.next()) {
              key = keyIt.value;

              if ( // Skip the special useSWRInfinite keys.
              !key.startsWith('$inf$') && keyFilter(cache.get(key)._k)) {
                matchedKeys.push(key);
              }
            }

            return _context2.abrupt("return", Promise.all(matchedKeys.map(mutateByKey)));

          case 17:
            return _context2.abrupt("return", mutateByKey(_key));

          case 18:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _internalMutate.apply(this, arguments);
}

var revalidateAllKeys = function revalidateAllKeys(revalidators, type) {
  for (var key in revalidators) {
    if (revalidators[key][0]) revalidators[key][0](type);
  }
};

var initCache = function initCache(provider, options) {
  // The global state for a specific provider will be used to deduplicate
  // requests and store listeners. As well as a mutate function that is bound to
  // the cache.
  // The provider's global state might be already initialized. Let's try to get the
  // global state associated with the provider first.
  if (!SWRGlobalState.has(provider)) {
    var opts = mergeObjects(defaultConfigOptions, options); // If there's no global state bound to the provider, create a new one with the
    // new mutate function.

    var EVENT_REVALIDATORS = {};

    var _mutate = internalMutate.bind(UNDEFINED, provider);

    var unmount = noop;
    var subscriptions = {};

    var subscribe = function subscribe(key, callback) {
      var subs = subscriptions[key] || [];
      subscriptions[key] = subs;
      subs.push(callback);
      return function () {
        return subs.splice(subs.indexOf(callback), 1);
      };
    };

    var setter = function setter(key, value, prev) {
      provider.set(key, value);
      var subs = subscriptions[key];

      if (subs) {
        for (var i = subs.length; i--;) {
          subs[i](prev, value);
        }
      }
    };

    var initProvider = function initProvider() {
      if (!SWRGlobalState.has(provider)) {
        // Update the state if it's new, or if the provider has been extended.
        SWRGlobalState.set(provider, [EVENT_REVALIDATORS, {}, {}, {}, _mutate, setter, subscribe]);

        if (!IS_SERVER) {
          // When listening to the native events for auto revalidations,
          // we intentionally put a delay (setTimeout) here to make sure they are
          // fired after immediate JavaScript executions, which can be
          // React's state updates.
          // This avoids some unnecessary revalidations such as
          // https://github.com/vercel/swr/issues/1680.
          var releaseFocus = opts.initFocus(setTimeout.bind(UNDEFINED, revalidateAllKeys.bind(UNDEFINED, EVENT_REVALIDATORS, FOCUS_EVENT)));
          var releaseReconnect = opts.initReconnect(setTimeout.bind(UNDEFINED, revalidateAllKeys.bind(UNDEFINED, EVENT_REVALIDATORS, RECONNECT_EVENT)));

          unmount = function unmount() {
            releaseFocus && releaseFocus();
            releaseReconnect && releaseReconnect(); // When un-mounting, we need to remove the cache provider from the state
            // storage too because it's a side-effect. Otherwise, when re-mounting we
            // will not re-register those event listeners.

            SWRGlobalState.delete(provider);
          };
        }
      }
    };

    initProvider(); // This is a new provider, we need to initialize it and setup DOM events
    // listeners for `focus` and `reconnect` actions.
    // We might want to inject an extra layer on top of `provider` in the future,
    // such as key serialization, auto GC, etc.
    // For now, it's just a `Map` interface without any modifications.

    return [provider, _mutate, initProvider, unmount];
  }

  return [provider, SWRGlobalState.get(provider)[4]];
}; // error retry


var onErrorRetry = function onErrorRetry(_, __, config, revalidate, opts) {
  var maxRetryCount = config.errorRetryCount;
  var currentRetryCount = opts.retryCount; // Exponential backoff

  var timeout = ~~((Math.random() + 0.5) * (1 << (currentRetryCount < 8 ? currentRetryCount : 8))) * config.errorRetryInterval;

  if (!isUndefined(maxRetryCount) && currentRetryCount > maxRetryCount) {
    return;
  }

  setTimeout(revalidate, timeout, opts);
};

var compare = function compare(currentData, newData) {
  return stableHash(currentData) == stableHash(newData);
}; // Default cache provider


var _initCache = initCache(new Map()),
    _initCache2 = _slicedToArray(_initCache, 2),
    cache = _initCache2[0],
    mutate = _initCache2[1]; // Default config


var defaultConfig = mergeObjects({
  // events
  onLoadingSlow: noop,
  onSuccess: noop,
  onError: noop,
  onErrorRetry: onErrorRetry,
  onDiscarded: noop,
  // switches
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  revalidateIfStale: true,
  shouldRetryOnError: true,
  // timeouts
  errorRetryInterval: slowConnection ? 10000 : 5000,
  focusThrottleInterval: 5 * 1000,
  dedupingInterval: 2 * 1000,
  loadingTimeout: slowConnection ? 5000 : 3000,
  // providers
  compare: compare,
  isPaused: function isPaused() {
    return false;
  },
  cache: cache,
  mutate: mutate,
  fallback: {}
}, // use web preset by default
preset);

var mergeConfigs = function mergeConfigs(a, b) {
  // Need to create a new object to avoid mutating the original here.
  var v = mergeObjects(a, b); // If two configs are provided, merge their `use` and `fallback` options.

  if (b) {
    var u1 = a.use,
        f1 = a.fallback;
    var u2 = b.use,
        f2 = b.fallback;

    if (u1 && u2) {
      v.use = u1.concat(u2);
    }

    if (f1 && f2) {
      v.fallback = mergeObjects(f1, f2);
    }
  }

  return v;
};

var SWRConfigContext = createContext({});

var SWRConfig = function SWRConfig(props) {
  var value = props.value;
  var parentConfig = useContext(SWRConfigContext);
  var isFunctionalConfig = isFunction(value);
  var config = useMemo(function () {
    return isFunctionalConfig ? value(parentConfig) : value;
  }, [isFunctionalConfig, parentConfig, value]); // Extend parent context values and middleware.

  var extendedConfig = useMemo(function () {
    return isFunctionalConfig ? config : mergeConfigs(parentConfig, config);
  }, [isFunctionalConfig, parentConfig, config]); // Should not use the inherited provider.

  var provider = config && config.provider; // Use a lazy initialized state to create the cache on first access.

  var _useState = useState(function () {
    return provider ? initCache(provider(extendedConfig.cache || cache), config) : UNDEFINED;
  }),
      _useState2 = _slicedToArray(_useState, 1),
      cacheContext = _useState2[0]; // Override the cache if a new provider is given.


  if (cacheContext) {
    extendedConfig.cache = cacheContext[0];
    extendedConfig.mutate = cacheContext[1];
  } // Unsubscribe events.


  useIsomorphicLayoutEffect(function () {
    if (cacheContext) {
      cacheContext[2] && cacheContext[2]();
      return cacheContext[3];
    }
  }, []);
  return createElement(SWRConfigContext.Provider, mergeObjects(props, {
    value: extendedConfig
  }));
}; // @ts-expect-error


var enableDevtools = isWindowDefined && window.__SWR_DEVTOOLS_USE__;
var use = enableDevtools ? window.__SWR_DEVTOOLS_USE__ : [];

var setupDevTools = function setupDevTools() {
  if (enableDevtools) {
    // @ts-expect-error
    window.__SWR_DEVTOOLS_REACT__ = React;
  }
};

var normalize = function normalize(args) {
  return isFunction(args[1]) ? [args[0], args[1], args[2] || {}] : [args[0], null, (args[1] === null ? args[2] : args[1]) || {}];
};

var useSWRConfig = function useSWRConfig() {
  return mergeObjects(defaultConfig, useContext(SWRConfigContext));
};

var preload = function preload(key_, fetcher) {
  var key = serialize(key_)[0];

  var _SWRGlobalState$get = SWRGlobalState.get(cache),
      _SWRGlobalState$get2 = _slicedToArray(_SWRGlobalState$get, 4),
      PRELOAD = _SWRGlobalState$get2[3]; // Prevent preload to be called multiple times before used.


  if (PRELOAD[key]) return PRELOAD[key];
  var req = fetcher(key_);
  PRELOAD[key] = req;
  return req;
};

var middleware = function middleware(useSWRNext) {
  return function (key_, fetcher_, config) {
    // fetcher might be a sync function, so this should not be an async function
    var fetcher = fetcher_ && function () {
      var key = serialize(key_)[0];

      var _SWRGlobalState$get3 = SWRGlobalState.get(cache),
          _SWRGlobalState$get4 = _slicedToArray(_SWRGlobalState$get3, 4),
          PRELOAD = _SWRGlobalState$get4[3];

      var req = PRELOAD[key];

      if (req) {
        delete PRELOAD[key];
        return req;
      }

      return fetcher_.apply(void 0, arguments);
    };

    return useSWRNext(key_, fetcher, config);
  };
};

var BUILT_IN_MIDDLEWARE = use.concat(middleware); // It's tricky to pass generic types as parameters, so we just directly override
// the types here.

var withArgs = function withArgs(hook) {
  return function useSWRArgs() {
    // Get the default and inherited configuration.
    var fallbackConfig = useSWRConfig(); // Normalize arguments.

    for (var _len2 = arguments.length, args = new Array(_len2), _key3 = 0; _key3 < _len2; _key3++) {
      args[_key3] = arguments[_key3];
    }

    var _normalize = normalize(args),
        _normalize2 = _slicedToArray(_normalize, 3),
        key = _normalize2[0],
        fn = _normalize2[1],
        _config = _normalize2[2]; // Merge configurations.


    var config = mergeConfigs(fallbackConfig, _config); // Apply middleware

    var next = hook;
    var use = config.use;
    var middleware = (use || []).concat(BUILT_IN_MIDDLEWARE);

    for (var i = middleware.length; i--;) {
      next = middleware[i](next);
    }

    return next(key, fn || config.fetcher || null, config);
  };
};
/**
 * An implementation of state with dependency-tracking.
 */


var useStateWithDeps = function useStateWithDeps(state) {
  var rerender = useState({})[1];
  var unmountedRef = useRef(false);
  var stateRef = useRef(state); // If a state property (data, error, or isValidating) is accessed by the render
  // function, we mark the property as a dependency so if it is updated again
  // in the future, we trigger a rerender.
  // This is also known as dependency-tracking.

  var stateDependenciesRef = useRef({
    data: false,
    error: false,
    isValidating: false
  });
  /**
  * @param payload To change stateRef, pass the values explicitly to setState:
  * @example
  * ```js
  * setState({
  *   isValidating: false
  *   data: newData // set data to newData
  *   error: undefined // set error to undefined
  * })
  *
  * setState({
  *   isValidating: false
  *   data: undefined // set data to undefined
  *   error: err // set error to err
  * })
  * ```
  */

  var setState = useCallback(function (payload) {
    var shouldRerender = false;
    var currentState = stateRef.current;

    for (var _ in payload) {
      var k = _; // If the property has changed, update the state and mark rerender as
      // needed.

      if (currentState[k] !== payload[k]) {
        currentState[k] = payload[k]; // If the property is accessed by the component, a rerender should be
        // triggered.

        if (stateDependenciesRef.current[k]) {
          shouldRerender = true;
        }
      }
    }

    if (shouldRerender && !unmountedRef.current) {
      if (IS_REACT_LEGACY) {
        rerender({});
      } else {
        React.startTransition(function () {
          return rerender({});
        });
      }
    }
  }, // config.suspense isn't allowed to change during the lifecycle
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  useIsomorphicLayoutEffect(function () {
    unmountedRef.current = false;
    return function () {
      unmountedRef.current = true;
    };
  });
  return [stateRef, stateDependenciesRef.current, setState];
}; // Add a callback function to a list of keyed callback functions and return
// the unsubscribe function.


var subscribeCallback = function subscribeCallback(key, callbacks, callback) {
  var keyedRevalidators = callbacks[key] || (callbacks[key] = []);
  keyedRevalidators.push(callback);
  return function () {
    var index = keyedRevalidators.indexOf(callback);

    if (index >= 0) {
      // O(1): faster than splice
      keyedRevalidators[index] = keyedRevalidators[keyedRevalidators.length - 1];
      keyedRevalidators.pop();
    }
  };
}; // Create a custom hook with a middleware


var withMiddleware = function withMiddleware(useSWR, middleware) {
  return function () {
    for (var _len3 = arguments.length, args = new Array(_len3), _key4 = 0; _key4 < _len3; _key4++) {
      args[_key4] = arguments[_key4];
    }

    var _normalize3 = normalize(args),
        _normalize4 = _slicedToArray(_normalize3, 3),
        key = _normalize4[0],
        fn = _normalize4[1],
        config = _normalize4[2];

    var uses = (config.use || []).concat(middleware);
    return useSWR(key, fn, _objectSpread(_objectSpread({}, config), {}, {
      use: uses
    }));
  };
};

setupDevTools();
export { IS_REACT_LEGACY, IS_SERVER, OBJECT, SWRConfig, SWRGlobalState, UNDEFINED, cache, compare, createCacheHelper, defaultConfig, defaultConfigOptions, getTimestamp, hasRequestAnimationFrame, initCache, internalMutate, isDocumentDefined, isFunction, isUndefined, isWindowDefined, mergeConfigs, mergeObjects, mutate, noop, normalize, preload, preset, rAF, constants as revalidateEvents, serialize, slowConnection, stableHash, subscribeCallback, useIsomorphicLayoutEffect, useSWRConfig, useStateWithDeps, withArgs, withMiddleware };